import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, ChevronLeft, ChevronRight, Gauge, Calendar, Fuel, Settings2, Palette, Zap, ShieldCheck, Truck, Wallet, Check, QrCode, Lock, Send, User, Phone, LifeBuoy, ArrowRight, Download } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import QrShareDialog from "@/components/QrShareDialog";
import VehicleLeadForm from "@/components/VehicleLeadForm";
import RichContent, { VideoEmbed } from "@/components/RichContent";
import { generateVehiclePdf } from "@/lib/vehiclePdf";
import FacebookShareButton from "@/components/FacebookShareButton";

const fmtPrice = (v) =>
  v && v > 0 ? new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : "Prix sur demande";
const fmtKm = (v) => new Intl.NumberFormat("fr-BE").format(v || 0) + " km";

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, pick } = useLang();
  const [v, setV] = useState(null);
  const [i, setI] = useState(0);
  const [qrOpen, setQrOpen] = useState(false);
  const [site, setSite] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => nav("/stock"));
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    // Track view (fire-and-forget)
    if (id) api.post(`/vehicles/${id}/view`).catch(() => {});
  }, [id, nav]);

  if (!v) return <div className="min-h-screen bg-[#0D0D0D] pt-32 text-center text-white/50">{t.common.loading}</div>;

  const images = v.images?.length ? v.images : ["https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4807217354.jpg"];

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: `${v.brand} ${v.model}`, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Lien copié"); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      await generateVehiclePdf({ ...v, description: pick(v.description) || v.description }, site);
      toast.success("Fiche PDF téléchargée");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const vehicleShareUrl = typeof window !== "undefined" ? `${window.location.origin}/stock/${v.id}` : "";
  const priceLabel = v.price && v.price > 0 ? `${new Intl.NumberFormat("fr-BE").format(v.price)} €` : "Prix sur demande";
  const waMessage = `${v.brand} ${v.model} · ${v.year} · ${new Intl.NumberFormat("fr-BE").format(v.mileage || 0)} km · ${priceLabel}\nKV International — Rhode-Saint-Genèse`;

  return (
    <div data-testid="vehicle-detail" className="pt-24 pb-24 bg-[#0D0D0D] min-h-screen">
      <SEO
        title={`${v.brand} ${v.model} ${v.year} occasion Prestige — ${v.price && !v.price_confidential ? `${new Intl.NumberFormat("fr-BE").format(v.price)} €` : "Prix sur demande"} · Luxe Belgique`}
        description={`${v.brand} ${v.model} ${v.year} d'occasion prestige · ${v.mileage?.toLocaleString?.("fr-BE") || 0} km · ${v.power_hp} ch · ${v.fuel_type} · ${v.transmission}${v.color ? ` · ${v.color}` : ""} — ${v.price && !v.price_confidential ? `${new Intl.NumberFormat("fr-BE").format(v.price)} €` : "Prix sur demande"}. Voiture de luxe sélectionnée par KV International, expert Porsche & prestige à Rhode-Saint-Genèse (Belgique). Showroom sur rendez-vous, livraison Europe, stage simulateur pro.`}
        image={v.images?.[0]}
        type="product"
        schemaType="Vehicle"
        vehicle={v}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link to="/stock" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C8A75A] mb-8">
          <ArrowLeft className="w-3 h-3" /> {t.vehicle.back}
        </Link>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
          {/* Gallery */}
          <div>
            {(() => {
              const list = [];
              if (v.video_url) list.push({ url: v.video_url, label: "Présentation" });
              if (Array.isArray(v.videos)) v.videos.forEach((entry, i) => {
                if (entry?.url) list.push({ url: entry.url, label: entry.label || `Vidéo ${list.length + 1}` });
              });
              if (!list.length) return null;
              if (list.length === 1) {
                return (
                  <div className="mb-3" data-testid="vehicle-video">
                    <VideoEmbed url={list[0].url} />
                  </div>
                );
              }
              return <MultiVideoPlayer videos={list} />;
            })()}
            <div className="relative aspect-[4/3] bg-[#1E1E1E] rounded-md overflow-hidden group">
              <img
                data-testid="gallery-main"
                key={images[i]}
                src={images[i]}
                alt={`${v.brand} ${v.model} ${v.year} — photo ${i + 1} sur ${images.length}`}
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover slide-fade"
              />
              {/* Bouton overlay : ESSAYEZ-MOI SUR PISTE */}
              <Link
                to="/simulator"
                data-testid="vd-image-sim-cta"
                aria-label="Essayez-moi sur piste — Stage simulateur"
                className="absolute top-4 right-4 z-30 inline-flex items-center gap-2 bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 px-4 py-2.5 rounded-md text-[11px] md:text-xs uppercase tracking-[0.2em] font-bold shadow-[0_8px_28px_-4px_rgba(200,167,90,0.7)] ring-1 ring-black/20"
              >
                <LifeBuoy className="w-4 h-4" strokeWidth={1.8} />
                Essayez-moi sur piste
              </Link>
              {/* Bouton partage Facebook — superposé en haut à gauche, style prestige */}
              <FacebookShareButton
                testid="vd-image-fb-share"
                label="Partager sur Facebook"
                variant="icon-prestige"
                className="absolute top-4 left-4 z-30"
              />
              {images.length > 1 && (
                <>
                  <button aria-label="prev" onClick={() => setI((p) => (p - 1 + images.length) % images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[#C8A75A] hover:text-black text-white flex items-center justify-center transition-colors z-10">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button aria-label="next" onClick={() => setI((p) => (p + 1) % images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-[#C8A75A] hover:text-black text-white flex items-center justify-center transition-colors z-10">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {images.slice(0, 10).map((im, idx) => (
                  <button key={idx} onClick={() => setI(idx)} data-testid={`thumb-${idx}`} data-active={idx === i}
                    className={`aspect-[4/3] overflow-hidden rounded-sm border thumb-fade ${idx === i ? "border-[#C8A75A]" : "border-white/10"}`}>
                    <img src={im} alt={`${v.brand} ${v.model} — miniature ${idx + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-3">{v.brand}</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight font-light text-white mb-3">{v.model}</h1>
            <p className="text-white/50 text-sm mb-6">{v.year} · {v.body_type}</p>
            {v.status === "sold" ? (
              <div data-testid="vd-sold-badge" className="mb-8">
                <p className="font-display text-2xl md:text-3xl text-white/85 leading-tight">Trop tard, déjà sur nos routes.</p>
                <p className="text-white/50 text-xs mt-2 tracking-wide">Cette auto a trouvé preneur · Envie du même modèle ? Voir le formulaire ci-dessous</p>
              </div>
            ) : v.price_confidential ? (
              <div data-testid="vd-price-confidential" className="mb-8">
                <p className="font-display text-2xl md:text-3xl text-[#C8A75A] flex items-center gap-3">
                  <Lock className="w-6 h-6" strokeWidth={1.4} />
                  Prix sur demande — pièce rare
                </p>
                <p className="text-white/50 text-xs mt-2 tracking-wide">Tarif confidentiel · Recevez le prix par email ci-dessous</p>
              </div>
            ) : (
              <p data-testid="vd-price" className="font-display text-4xl text-[#C8A75A] mb-8">{fmtPrice(v.price)}</p>
            )}

            <div className="grid grid-cols-2 gap-3 mb-8">
              <Spec icon={Calendar} label={t.vehicle.year} value={v.year} />
              <Spec icon={Gauge} label="KM" value={fmtKm(v.mileage)} />
              <Spec icon={Zap} label={t.vehicle.power} value={`${v.power_hp} ${t.vehicle.hp}`} />
              <Spec icon={Fuel} label={t.vehicle.fuel} value={v.fuel_type} />
              <Spec icon={Settings2} label={t.vehicle.transmission} value={v.transmission} />
              <Spec icon={Palette} label={t.vehicle.color} value={v.color || "-"} />
              {v.warranty_months > 0 && <Spec icon={ShieldCheck} label={t.vehicle.warranty} value={`${v.warranty_months} ${t.vehicle.months}`} />}
              {v.delivery_europe && <Spec icon={Truck} label={t.vehicle.delivery} value="✓" />}
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
              <Button data-testid="vd-request-info" onClick={() => nav(`/contact?vehicle=${v.id}&type=info`)} className="bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs px-6 py-6 rounded-md">
                {t.vehicle.request_info}
              </Button>
              <Button data-testid="vd-book" onClick={() => nav(`/contact?vehicle=${v.id}&type=test_drive`)} variant="outline" className="border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase tracking-widest text-xs px-6 py-6 rounded-md">
                {t.vehicle.book_appt}
              </Button>
              {v.financing_available && (
                <Button data-testid="vd-financing" onClick={() => nav(`/contact?vehicle=${v.id}&type=financing`)} variant="outline" className="border-white/20 text-white hover:bg-white/5 uppercase tracking-widest text-xs px-6 py-6 rounded-md">
                  <Wallet className="w-4 h-4 mr-2" /> {t.vehicle.financing_btn}
                </Button>
              )}
              <button data-testid="vd-share" onClick={share} aria-label="share" className="w-12 h-12 border border-white/20 rounded-md flex items-center justify-center text-white/70 hover:text-[#C8A75A] hover:border-[#C8A75A] transition-colors" title="Partager (natif)">
                <Share2 className="w-4 h-4" />
              </button>
              <button
                data-testid="vd-pdf-download"
                onClick={downloadPdf}
                disabled={pdfLoading}
                aria-label="Télécharger la fiche PDF"
                title="Télécharger la fiche en PDF"
                className="inline-flex items-center gap-2 h-12 border border-[#C8A75A]/50 bg-[#C8A75A]/10 hover:bg-[#C8A75A] hover:text-black text-[#C8A75A] rounded-md px-4 uppercase tracking-widest text-xs transition-colors disabled:opacity-60"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{pdfLoading ? "Génération…" : "Fiche PDF"}</span>
              </button>
              <button data-testid="vd-qr" onClick={() => setQrOpen(true)} aria-label="Partager sur WhatsApp via QR" className="inline-flex items-center gap-2 h-12 border border-[#25D366]/50 bg-[#25D366]/10 hover:bg-[#25D366] hover:text-black text-[#25D366] rounded-md px-4 uppercase tracking-widest text-xs transition-colors" title="Partager sur WhatsApp">
                <QrCode className="w-4 h-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
            </div>

            {/* Lead capture form — mode dépend du statut/prix confidentiel */}
            <div className="mt-8">
              <VehicleLeadForm
                vehicleId={v.id}
                brand={v.brand}
                model={v.model}
                mode={v.status === "sold" ? "sold" : v.price_confidential ? "confidential" : "available"}
              />
            </div>

            {/* Formulaire court de contact — nom, téléphone, message */}
            <VehicleContactForm vehicle={v} />
          </div>
        </div>

        {/* PRESTIGE CTA — Stage simulateur (visible sur TOUTES les fiches, sans exception) */}
        <Link
          to="/simulator"
          data-testid="vd-simulator-cta"
          style={{ display: "block", visibility: "visible", opacity: 1 }}
          className="group relative block mt-14 overflow-hidden rounded-lg border-2 border-[#C8A75A]/70 bg-gradient-to-r from-[#0D0D0D] via-[#141414] to-[#0D0D0D] px-8 py-8 md:py-10 shadow-[0_15px_60px_-20px_rgba(200,167,90,0.55)] hover:border-[#C8A75A] hover:shadow-[0_20px_70px_-15px_rgba(200,167,90,0.85)] transition-all duration-500"
        >
          <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-[#C8A75A]/15 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000 ease-out" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <span className="flex-shrink-0 w-16 h-16 rounded-full border-2 border-[#C8A75A]/60 bg-black/50 flex items-center justify-center text-[#C8A75A] group-hover:rotate-45 group-hover:border-[#C8A75A] transition-all duration-500">
              <LifeBuoy className="w-7 h-7" strokeWidth={1.4} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A75A] mb-2">Coaching sim pro · Perfectionnement circuit</p>
              <p className="font-display text-2xl md:text-3xl text-white leading-tight font-light">
                Envie de tester les limites de ce modèle&nbsp;?
              </p>
              <p className="text-white/70 text-base md:text-lg font-light mt-2">
                Découvrez notre Stage simulateur de conduite pro.
              </p>
            </div>
            <span className="flex-shrink-0 inline-flex items-center gap-3 border border-[#C8A75A] text-[#C8A75A] group-hover:bg-[#C8A75A] group-hover:text-black transition-colors duration-300 px-6 py-3 rounded-md text-xs uppercase tracking-widest font-semibold">
              Découvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </Link>

        <QrShareDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          title={`${v.brand} · ${v.year}`}
          subtitle={`${v.model} · ${priceLabel}`}
          url={vehicleShareUrl}
          waMessage={waMessage}
          downloadName={`kv-vehicle-${v.id}`}
        />

        {/* Description */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.vehicle.description}</p>
            <p className="text-white/70 text-base leading-relaxed whitespace-pre-line"><RichContent text={pick(v.description)} /></p>
            {v.history && (
              <>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mt-10 mb-4">{t.vehicle.history}</p>
                <p className="text-white/70 leading-relaxed">{v.history}</p>
              </>
            )}
          </div>
          {v.features?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.vehicle.features}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {v.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-[#C8A75A] mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-4">
      <div className="flex items-center gap-2 text-white/40 text-[10px] uppercase tracking-[0.2em] mb-1">
        <Icon className="w-3.5 h-3.5 text-[#C8A75A]" /> {label}
      </div>
      <p className="text-white text-sm font-medium">{value}</p>
    </div>
  );
}

function MultiVideoPlayer({ videos }) {
  const [active, setActive] = useState(0);
  const current = videos[active];
  return (
    <div className="mb-3" data-testid="vehicle-multi-video">
      <VideoEmbed key={current.url} url={current.url} />
      <div className="flex flex-wrap gap-2 -mt-4 mb-1">
        {videos.map((v, i) => (
          <button
            key={`${v.url}-${i}`}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`video-tab-${i}`}
            className={`text-[10px] uppercase tracking-widest px-3 py-2 rounded-md transition-colors border ${
              active === i
                ? "bg-[#C8A75A] text-black border-[#C8A75A] font-semibold"
                : "bg-[#1E1E1E] text-white/60 border-white/10 hover:border-[#C8A75A]/50 hover:text-white"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function VehicleContactForm({ vehicle }) {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: "",
        phone: form.phone,
        subject: `Question sur ${vehicle.brand} ${vehicle.model} (${vehicle.year})`,
        message: `Véhicule consulté : ${vehicle.brand} ${vehicle.model} (${vehicle.year}) — /stock/${vehicle.id}\n\nMessage :\n${form.message || "(pas de message)"}`,
        request_type: "info",
        vehicle_id: vehicle.id,
      });
      toast.success("Merci — nous vous rappelons rapidement.");
      setSent(true);
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur — réessayez.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="vd-contact-form-success" className="mt-6 bg-[#1E1E1E] border border-[#C8A75A]/40 rounded-md p-6">
        <p className="font-display text-xl text-white">Message reçu, merci.</p>
        <p className="text-white/60 text-sm mt-2">Un membre de l'équipe KV vous rappelle sous 24 h ouvrables.</p>
      </div>
    );
  }

  return (
    <form data-testid="vd-contact-form" onSubmit={submit} className="mt-6 bg-[#1E1E1E] border border-white/10 rounded-md p-6 space-y-3">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]">Une question ? On vous rappelle.</p>
      <p className="font-display text-xl text-white leading-tight">Poser une question sur ce véhicule</p>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.4} />
          <input required data-testid="vd-contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet" className="w-full bg-[#0D0D0D] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md pl-9 pr-3 py-3 text-sm text-white placeholder:text-white/30" />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" strokeWidth={1.4} />
          <input required type="tel" data-testid="vd-contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="w-full bg-[#0D0D0D] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md pl-9 pr-3 py-3 text-sm text-white placeholder:text-white/30" />
        </div>
      </div>
      <textarea data-testid="vd-contact-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Votre message (optionnel)" className="w-full bg-[#0D0D0D] border border-white/10 focus:border-[#C8A75A]/60 outline-none rounded-md px-3 py-3 text-sm text-white placeholder:text-white/30 resize-none" />
      <button type="submit" disabled={sending} data-testid="vd-contact-submit" className="w-full bg-white text-black hover:bg-[#C8A75A] transition-colors font-semibold px-6 py-3 rounded-md text-xs uppercase tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {sending ? "Envoi…" : "Envoyer ma question"}
        {!sending && <Send className="w-4 h-4" />}
      </button>
      <p className="text-white/40 text-[10px] text-center leading-relaxed">Réponse sous 24 h ouvrables · Rhode-Saint-Genèse</p>
    </form>
  );
}
